import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@shared/lib/utils';

interface PageHeroProps {
  /** Small line above the title (e.g. a greeting). */
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned actions rendered on the gradient. */
  actions?: ReactNode;
  className?: string;
}

/**
 * Branded gradient hero used at the top of key admin pages (Step 6). Fully
 * token-driven — the gradient derives from --brand-primary / --brand-accent, so
 * a rebrand flows through every hero at once. White foreground throughout.
 */
export function PageHero({ eyebrow, title, subtitle, actions, className }: PageHeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLImageElement>(null);

  // DVD-screensaver-style bounce: the mark drifts and bounces off the hero edges.
  useEffect(() => {
    const hero = heroRef.current;
    const mark = markRef.current;
    if (!hero || !mark) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mark.style.transform = 'translate(0px, 0px)';
      return;
    }

    let x = 24;
    let y = 12;
    let vx = 0.45;
    let vy = 0.32;
    let angle = 0;
    let raf = 0;

    const step = () => {
      const maxX = Math.max(0, hero.clientWidth - mark.offsetWidth);
      const maxY = Math.max(0, hero.clientHeight - mark.offsetHeight);
      x += vx;
      y += vy;
      // Bounce off the walls; nudge the speed a touch for a less mechanical path.
      if (x <= 0) { x = 0; vx = Math.abs(vx); vy += (Math.random() - 0.5) * 0.1; }
      else if (x >= maxX) { x = maxX; vx = -Math.abs(vx); vy += (Math.random() - 0.5) * 0.1; }
      if (y <= 0) { y = 0; vy = Math.abs(vy); vx += (Math.random() - 0.5) * 0.1; }
      else if (y >= maxY) { y = maxY; vy = -Math.abs(vy); vx += (Math.random() - 0.5) * 0.1; }
      // Keep speeds sane (and gentle).
      vx = Math.max(-0.65, Math.min(0.65, vx)) || 0.4;
      vy = Math.max(-0.5, Math.min(0.5, vy)) || 0.3;
      // Slow continuous spin of the mark itself (~one turn every ~40s).
      angle = (angle + 0.15) % 360;
      mark.style.transform = `translate(${x}px, ${y}px) rotate(${angle}deg)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={heroRef}
      className={cn(
        'relative overflow-hidden rounded-2xl p-6 text-white sm:p-8',
        className,
      )}
      style={{ background: 'var(--brand-block)' }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '26px 26px',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full opacity-40 blur-3xl"
        style={{ background: 'color-mix(in oklab, white, transparent 60%)' }}
      />
      {/* Orlenn mark watermark — bounces around the hero (DVD-screensaver style),
          driven by the effect above via markRef. No rotation. */}
      <img
        ref={markRef}
        aria-hidden
        src="/brand/orlenn-mark.svg"
        alt=""
        draggable={false}
        className="pointer-events-none absolute left-0 top-0 hidden h-20 w-20 select-none opacity-[0.14] will-change-transform sm:block"
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-sm font-medium text-white/70">{eyebrow}</p>
          )}
          <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 text-sm text-white/80">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/** White pill button for use on the gradient hero. */
export function HeroPrimaryButton({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-lg bg-white px-3.5 py-2 text-sm font-semibold text-[#274054] shadow-sm transition-transform hover:-translate-y-px',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Glass icon button for use on the gradient hero. */
export function HeroGlassButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/25 transition-colors hover:bg-white/25 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
    >
      {children}
    </button>
  );
}
